import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, useLocation, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Mail } from "lucide-react";
import {AutoTranslate} from "@/lib/autoTranslate.tsx"

export default function VerifyEmail() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");

  useEffect(() => {
    // Get email from navigation state or session
    const emailFromState = location.state?.email;
    if (emailFromState) {
      setEmail(emailFromState);
    } else {
      // Fallback: get from session
      const checkEmail = async () => {
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.user?.email) {
          setEmail(session.user.email);
        }
      };
      checkEmail();
    }

    // Check if user came from email link verification
    const token = searchParams.get('token');
    const type = searchParams.get('type');

    if (token && type === 'email') {
      verifyEmailFromLink(token);
    }
  }, [searchParams, location]);

  const verifyEmailFromLink = async (token: string) => {
    setLoading(true);
    try {
      const { error } = await supabase.auth.verifyOtp({
        token_hash: token,
        type: 'email'
      });

      if (error) throw error;

      toast({
        title: "Email verified!",
        description: "Your email has been verified successfully.",
      });

      setTimeout(() => navigate("/"), 1500);
    } catch (error: any) {
      toast({
        title: "Verification failed",
        description: error.message,
        variant: "destructive",
      });
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-main p-4">
      <div className="w-full max-w-md text-center space-y-6">
        <div className="flex justify-center mb-8">
          <div className="p-6 bg-muted/50 rounded-full border-2 border-primary/20">
            <Mail className="h-16 w-16 text-primary" />
          </div>
        </div>

        <div className="space-y-3">
          <h1 className="text-3xl font-bold text-foreground">{AutoTranslate("Verify Your Email")}</h1>
          <p className="text-base text-muted-foreground leading-relaxed">
            {AutoTranslate("We've sent an email to")} <strong className="text-foreground">{email}</strong>.
            {AutoTranslate("It is crucial to confirm your email address to ensure the security of your account.")}
          </p>
        </div>

        <div className="pt-4">
          <Link
            to="/auth?tab=signup"
            className="font-bold text-foreground hover:underline transition-all inline-block"
          >
            {AutoTranslate("Resend Email")}
          </Link>
        </div>

        {loading && (
          <p className="text-sm text-muted-foreground">{AutoTranslate("Verifying...")}</p>
        )}
      </div>
    </div>
  );
}
